"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 448 512" fill="currentColor" className={className}><path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"/></svg>
);

interface CardsList {
  id: string;
  name: string;
  slug?: string;
  card_image: string;
}

interface CategoryData {
  category: string;
  slug: string;
  cards: CardsList[];
}

interface Props {
  initialData: CategoryData[];
}

// Map category names to emojis for personality
const categoryEmojis: Record<string, string> = {
  'Birthday': '🎂',
  'Farewell': '👋',
  'Get Well Soon': '💐',
  'Thank You': '🙏',
  'Congratulations': '🎉',
  'Wedding': '💒',
  'Baby Shower': '🍼',
  'Anniversary': '💕',
  'Retirement': '🏖️',
  'Graduation': '🎓',
  'Christmas': '🎄',
  'New Year': '🎆',
};

const getEmoji = (category: string): string => {
  for (const [key, emoji] of Object.entries(categoryEmojis)) {
    if (category.toLowerCase().includes(key.toLowerCase())) return emoji;
  }
  return '✨';
};

const CardDesignsClient: React.FC<Props> = ({ initialData }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>(initialData[0]?.category || "");
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string>(initialData[0]?.slug || "");
  const [displayedCards, setDisplayedCards] = useState<CardsList[]>(initialData[0]?.cards || []);

  const selectCategory = (category: string) => {
    const categoryObj = initialData.find((cat) => cat.category === category);
    if (categoryObj) {
      setSelectedCategory(category);
      setSelectedCategorySlug(categoryObj.slug);
      setDisplayedCards(categoryObj.cards);
    }
  };

  const categories = initialData.map(c => c.category);

  return (
    <section className="py-10 md:py-12 bg-paper border-t border-gray-100">
      <div className="content-shell">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-heart-600 mb-3">
            Card designs
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900 tracking-tight mb-2">
            Beautiful Cards for Every Mood, Team, and Moment
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Choose from modern, cute, professional, funny, heartfelt, and minimal card designs. Whether the moment is formal, emotional, playful, or personal, there is a card that fits the feeling.

          </p>
        </div>

        {/* Filter buttons with emojis */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10 px-2">
            {categories.map((category) => (
              <button
                key={category}
                className={`px-4 py-2.5 rounded-full border text-xs sm:text-sm font-semibold transition-all duration-300 flex items-center gap-1.5 active:scale-95 ${
                  category === selectedCategory
                    ? "bg-gradient-to-r from-indigo-600 to-violet-600 border-transparent text-white shadow-lg shadow-indigo-500/25 scale-105"
                    : "bg-white/80 backdrop-blur-md border-slate-200/80 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50 hover:text-indigo-600 shadow-xs"
                }`}
                onClick={() => selectCategory(category)}
                aria-pressed={category === selectedCategory}
              >
                <span aria-hidden="true">{getEmoji(category)}</span>
                <span>{category}</span>
              </button>
            ))}
          </div>
        )}

        {/* Card grid — portrait 5:7 ratio cards that look like real greeting cards */}
        {displayedCards.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 sm:gap-6">
            {displayedCards.map((card) => (
              <Link
                key={card.id}
                href={`/create/${card.slug || card.id}`}
                className="group block relative"
              >
                {/* Card outer wrapper with card fold effect & 3D shadow */}
                <div className="relative bg-white rounded-2xl p-2.5 shadow-sm hover:shadow-2xl hover:shadow-indigo-500/15 border border-slate-200/70 hover:border-indigo-300 transition-all duration-300 hover:-translate-y-2 group-hover:rotate-1">
                  
                  <div className="relative aspect-[5/7] w-full overflow-hidden rounded-xl bg-slate-100">
                    <Image
                      src={card.card_image}
                      alt={card.name}
                      fill
                      sizes="(min-width: 1024px) 200px, (min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw"
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                      loading="lazy"
                    />
                    
                    {/* Gradient Overlay & Animated CTA on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-3">
                      <span className="text-white text-xs font-semibold bg-white/20 backdrop-blur-md border border-white/30 px-3 py-2 rounded-xl w-full text-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 flex items-center justify-center gap-1.5">
                        <span>Personalize Card</span>
                        <ArrowRightIcon className="w-3 h-3" />
                      </span>
                    </div>
                  </div>

                  {/* Card Title Label below image */}
                  <div className="mt-2.5 px-1 pb-0.5 flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                      {card.name}
                    </h3>
                    
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* View all link */}
        {selectedCategory && displayedCards.length > 0 && (
          <div className="text-center mt-10">
            <Link
              href={`/categories/${selectedCategorySlug}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:text-indigo-600 hover:border-indigo-300 hover:shadow-md transition-all duration-200 group"
            >
              <span>View All {selectedCategory} cards</span>
              <ArrowRightIcon className="w-4 h-4 text-indigo-600 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CardDesignsClient;
